/**
 * THE CHART, and where discovery comes from.
 *
 * Discovery is not a flag. Nothing in the codebase can forget to set it, because it is
 * not set: for every place, this file SOLVES for the smallest track distance at which
 * the boat's natural drift brings that place within the sighting radius. That number is
 * a fact about the geometry of the track and the place, and a place is discovered
 * exactly when the player's progression has carried the boat at least that far.
 *
 * Two consequences that are the point of doing it this way:
 *
 *  1. An absent player discovers places on exactly the same schedule as a present one,
 *     because both schedules are the same pure function of the same progression number.
 *  2. The chart can never offer a place the player has not progressed to, so auto-sail
 *     cannot be used to skip the progression. The set of positions the boat can occupy
 *     is bounded by how far it has drifted.
 *
 * THE SOLVE IS BRACKETED BEFORE IT IS WALKED. A place at distance rho from home can only
 * be within R of the spiral where |A*theta - rho| <= R, because the spiral's own radius
 * at theta is exactly A*theta and no two points can be closer than the difference of
 * their radii. So the walk runs over theta in [(rho-R)/A, (rho+R)/A] and nowhere else:
 * about 480 samples for a place at the outer edge instead of 6,800 from theta = 0. That
 * bound is what makes `sightTrackDistanceOf` cheap enough for places.ts to call it
 * inside a layout solve.
 *
 * Within the bracket it is a deterministic walk at a step small enough that the boat
 * cannot cross the sighting disc between samples, then a fixed-count bisection on the
 * interval where the distance first drops through the radius.
 */

import { SIGHTING_RADIUS_LEAGUES, SPIRAL_LEAGUES_PER_RADIAN } from './constants';
import type { PlaceSeed } from './seed';
import { spiralArcLength, trackPositionAtTheta } from './track';
import type { Leagues, Vec2 } from './units';
import { distance, magnitude } from './units';
import type { WaterRegionId } from './regions';
import { regionAt } from './regions';

export interface ChartedPlace extends PlaceSeed {
  /**
   * Leagues along the track at which this place first came within the sighting radius.
   * Infinity means the natural drift never sees it, which is a layout defect.
   */
  readonly sightTrackDistance: Leagues;
  readonly region: WaterRegionId;
}

export interface Chart {
  readonly places: readonly ChartedPlace[];
  readonly byId: ReadonlyMap<string, ChartedPlace>;
  readonly sightingRadiusLeagues: Leagues;
  /** Track distance at which the last charted place is sighted. Leagues. */
  readonly lastSightTrackDistance: Leagues;
}

export interface BuildChartOptions {
  readonly sightingRadiusLeagues?: Leagues;
}

/** Bisection steps used to pin the sighting distance. 64 exhausts double precision. */
const SOLVE_STEPS = 64;

/** Hard stop on the bracketed walk, so a hostile position cannot spin the solver. */
const MAX_WALK_STEPS = 1_000_000;

const A = SPIRAL_LEAGUES_PER_RADIAN;

/**
 * The track distance at which the natural drift first brings `position` within `radius`.
 * Infinity when it never does.
 */
export function sightTrackDistanceOf(position: Vec2, radius: Leagues = SIGHTING_RADIUS_LEAGUES): Leagues {
  if (!Number.isFinite(position.x) || !Number.isFinite(position.y)) return Infinity;

  const rho = magnitude(position);
  if (rho <= radius) return 0;

  const thetaLo = Math.max(0, (rho - radius) / A);
  const thetaHi = (rho + radius) / A;

  let theta = thetaLo;
  if (distance(trackPositionAtTheta(theta), position) <= radius) return spiralArcLength(theta);

  let steps = 0;
  while (theta < thetaHi && steps < MAX_WALK_STEPS) {
    steps += 1;
    // Small enough that the boat cannot pass through the sighting disc unsampled: the
    // arc travelled per step is a quarter of the radius, not a whole diameter.
    const step = Math.max(radius / (4 * A * Math.sqrt(1 + theta * theta)), 1e-9);
    const next = theta + step;
    if (distance(trackPositionAtTheta(next), position) <= radius) {
      let lo = theta;
      let hi = next;
      for (let k = 0; k < SOLVE_STEPS; k += 1) {
        const mid = (lo + hi) / 2;
        if (distance(trackPositionAtTheta(mid), position) > radius) lo = mid;
        else hi = mid;
      }
      return spiralArcLength(hi);
    }
    theta = next;
  }
  return Infinity;
}

export function buildChart(seeds: readonly PlaceSeed[], options: BuildChartOptions = {}): Chart {
  const radius = options.sightingRadiusLeagues ?? SIGHTING_RADIUS_LEAGUES;

  const places: ChartedPlace[] = seeds.map((seed) => ({
    ...seed,
    sightTrackDistance: sightTrackDistanceOf(seed.position, radius),
    region: regionAt(seed.position),
  }));

  places.sort((a, b) => {
    if (a.sightTrackDistance !== b.sightTrackDistance) {
      return a.sightTrackDistance - b.sightTrackDistance;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  const byId = new Map<string, ChartedPlace>();
  for (const p of places) byId.set(p.id, p);

  let last = 0;
  for (const p of places) {
    if (Number.isFinite(p.sightTrackDistance)) last = Math.max(last, p.sightTrackDistance);
  }

  return { places, byId, sightingRadiusLeagues: radius, lastSightTrackDistance: last };
}

/** Places the natural drift never brings into sight. Should always be empty. */
export function unreachablePlaces(chart: Chart): readonly ChartedPlace[] {
  return chart.places.filter((p) => !Number.isFinite(p.sightTrackDistance));
}
